import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, User, Mail, Phone } from "lucide-react";

interface LeadCaptureFormProps {
  onSubmit: (data: LeadData) => void;
  isSubmitting?: boolean;
}

export interface LeadData {
  name: string;
  company?: string;
  email: string;
  countryCode: string;
  whatsapp: string;
}

const countryCodes = [
  { code: "+54", country: "Argentina", flag: "🇦🇷" },
  { code: "+1", country: "Estados Unidos", flag: "🇺🇸" },
  { code: "+52", country: "México", flag: "🇲🇽" },
  { code: "+34", country: "España", flag: "🇪🇸" },
  { code: "+57", country: "Colombia", flag: "🇨🇴" },
  { code: "+56", country: "Chile", flag: "🇨🇱" },
  { code: "+51", country: "Perú", flag: "🇵🇪" },
  { code: "+58", country: "Venezuela", flag: "🇻🇪" },
  { code: "+593", country: "Ecuador", flag: "🇪🇨" },
  { code: "+502", country: "Guatemala", flag: "🇬🇹" },
  { code: "+503", country: "El Salvador", flag: "🇸🇻" },
  { code: "+504", country: "Honduras", flag: "🇭🇳" },
  { code: "+505", country: "Nicaragua", flag: "🇳🇮" },
  { code: "+506", country: "Costa Rica", flag: "🇨🇷" },
  { code: "+507", country: "Panamá", flag: "🇵🇦" },
  { code: "+591", country: "Bolivia", flag: "🇧🇴" },
  { code: "+595", country: "Paraguay", flag: "🇵🇾" },
  { code: "+598", country: "Uruguay", flag: "🇺🇾" },
  { code: "+55", country: "Brasil", flag: "🇧🇷" },
  { code: "+49", country: "Alemania", flag: "🇩🇪" },
  { code: "+33", country: "Francia", flag: "🇫🇷" },
  { code: "+39", country: "Italia", flag: "🇮🇹" },
  { code: "+44", country: "Reino Unido", flag: "🇬🇧" },
];

const LeadCaptureForm = ({ onSubmit, isSubmitting = false }: LeadCaptureFormProps) => {
  const [formData, setFormData] = useState<LeadData>({
    name: "",
    email: "",
    countryCode: "+54",
    whatsapp: "",
  });
  const [errors, setErrors] = useState<Partial<LeadData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<LeadData> = {};

    // Name validation
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      newErrors.name = "El nombre es requerido";
    } else if (trimmedName.length > 100) {
      newErrors.name = "El nombre debe tener menos de 100 caracteres";
    }

    // Email validation
    const trimmedEmail = formData.email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!trimmedEmail) {
      newErrors.email = "El email es requerido";
    } else if (!emailRegex.test(trimmedEmail)) {
      newErrors.email = "Ingresá un email válido";
    } else if (trimmedEmail.length > 255) {
      newErrors.email = "El email debe tener menos de 255 caracteres";
    }

    // WhatsApp validation (now required)
    const trimmedWhatsapp = formData.whatsapp.trim();
    if (!trimmedWhatsapp) {
      newErrors.whatsapp = "El WhatsApp es requerido";
    } else if (!/^\d{6,15}$/.test(trimmedWhatsapp)) {
      newErrors.whatsapp = "Ingresá solo números (6-15 dígitos)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({
        name: formData.name.trim(),
        email: formData.email.trim(),
        countryCode: formData.countryCode,
        whatsapp: formData.whatsapp.trim(),
      });
    }
  };

  return (
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <span className="text-primary text-sm font-semibold tracking-widest uppercase mb-4 block">
          Último Paso
        </span>
        <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
          ¿A dónde enviamos tu{" "}
          <span className="text-gradient-gold">resultado?</span>
        </h2>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          Completá tus datos para ver tu diagnóstico personalizado y recibir recomendaciones exclusivas.
        </p>
      </motion.div>

      <motion.form
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        onSubmit={handleSubmit}
        className="max-w-md mx-auto space-y-6"
      >
        {/* Name */}
        <div className="text-left">
          <Label htmlFor="name" className="text-foreground font-medium mb-2 flex items-center gap-2">
            <User className="w-4 h-4 text-primary" />
            Nombre *
          </Label>
          <Input
            id="name"
            type="text"
            placeholder="Tu nombre completo"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={`bg-card border-border ${errors.name ? "border-destructive" : ""}`}
            maxLength={100}
          />
          {errors.name && (
            <p className="text-destructive text-sm mt-1">{errors.name}</p>
          )}
        </div>

        <div className="text-left">
          <Label htmlFor="email" className="text-foreground font-medium mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" />
            Email *
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="tu@email.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className={`bg-card border-border ${errors.email ? "border-destructive" : ""}`}
            maxLength={255}
          />
          {errors.email && (
            <p className="text-destructive text-sm mt-1">{errors.email}</p>
          )}
        </div>

        {/* WhatsApp with country code */}
        <div className="text-left">
          <Label htmlFor="whatsapp" className="text-foreground font-medium mb-2 flex items-center gap-2">
            <Phone className="w-4 h-4 text-primary" />
            WhatsApp *
          </Label>
          <div className="flex gap-2">
            <Select
              value={formData.countryCode}
              onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
            >
              <SelectTrigger className="w-32 bg-card border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {countryCodes.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    <span className="flex items-center gap-2">
                      <span>{country.flag}</span>
                      <span>{country.code}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="whatsapp"
              type="tel"
              placeholder="1123456789"
              value={formData.whatsapp}
              onChange={(e) => {
                // Only allow digits
                const value = e.target.value.replace(/\D/g, "");
                setFormData({ ...formData, whatsapp: value });
              }}
              className={`flex-1 bg-card border-border ${errors.whatsapp ? "border-destructive" : ""}`}
              maxLength={15}
            />
          </div>
          {errors.whatsapp && (
            <p className="text-destructive text-sm mt-1">{errors.whatsapp}</p>
          )}
        </div>

        <Button
          type="submit"
          variant="gold"
          size="xl"
          className="w-full group"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Guardando..." : "Ver mi Resultado"}
          {!isSubmitting && <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />}
        </Button>

        <p className="text-xs text-muted-foreground">
          Tus datos están protegidos. No compartimos tu información con terceros.
        </p>
      </motion.form>
    </div>
  );
};

export default LeadCaptureForm;
